import axiosInstance from './axios';
import { User, UsersParams, UsersResponse } from 'types/user';
import { mapFilterParams } from 'utils/filter';
import { generateIdempotencyKey } from 'utils/idempotency';

const filterFieldMap: Record<string, string> = {
  site: 'site.title',
};

export const userService = {
  getUsers: async (params: UsersParams): Promise<UsersResponse> => {
    const { page, limit, sortField, sortValue, ...rest } = params;
    const mappedParams = mapFilterParams(rest, filterFieldMap);

    const response = await axiosInstance.get(`/admin/users`, {
      params: {
        page,
        limit,
        sortField,
        sortValue,
        filters: '',
        ...mappedParams,
      },
    });
    return response.data;
  },
  getBlockedUsers: async (params: any): Promise<UsersResponse> => {
    const response = await axiosInstance.get('/admin/users/blocked', { params });
    return response.data;
  },
  getUserById: async (id: string): Promise<{ data: User }> => {
    const response = await axiosInstance.get(`/admin/users/${id}`);
    return response.data;
  },
  verifyEmail: async (id: string | number): Promise<any> => {
    const response = await axiosInstance.patch(`/admin/users/${id}/verify-email`);
    return response.data;
  },
  verifyPhone: async (id: string | number): Promise<any> => {
    const response = await axiosInstance.patch(`/admin/users/${id}/verify-phone`);
    return response.data;
  },
  getUserWheelSettings: async (userId: string, siteId: string | number): Promise<any> => {
    const response = await axiosInstance.get(`/admin/wheel/users/${userId}/settings`, {
      params: { siteId },
    });
    return response.data;
  },
  levelUp: async (
    userId: string | number,
    siteId: string | number,
    data?: { grantWheelSpin?: boolean },
  ): Promise<any> => {
    const response = await axiosInstance.patch(`/admin/users/${userId}/level/up`, data ?? null, {
      params: { siteId },
    });
    return response.data;
  },
  updateUserWheelSettings: async (userId: string, data: any): Promise<any> => {
    const response = await axiosInstance.patch(`/admin/wheel/users/${userId}/settings`, data);
    return response.data;
  },
  debitBalance: async (
    userId: string | number,
    data: any,
    idempotencyKey?: string,
  ): Promise<any> => {
    const key = idempotencyKey || generateIdempotencyKey();
    const response = await axiosInstance.post(`/admin/users/${userId}/balance/debit`, data, {
      headers: {
        'Idempotency-Key': key,
      },
    });
    return response.data;
  },
  creditBalance: async (
    userId: string | number,
    data: any,
    idempotencyKey?: string,
  ): Promise<any> => {
    const key = idempotencyKey || generateIdempotencyKey();
    const response = await axiosInstance.post(`/admin/users/${userId}/balance/credit`, data, {
      headers: {
        'Idempotency-Key': key,
      },
    });
    return response.data;
  },
  getOnlineUsers: async (params: UsersParams): Promise<UsersResponse> => {
    const { page, limit, sortField, sortValue, ...rest } = params;
    const mappedParams = mapFilterParams(rest, filterFieldMap);

    const response = await axiosInstance.get(`/admin/users/online`, {
      params: {
        page,
        limit,
        sortField,
        sortValue,
        filters: '',
        ...mappedParams,
      },
    });
    return response.data;
  },
  getUserSpins: async (userId: string, siteId: string | number, params?: any): Promise<any> => {
    const response = await axiosInstance.get(`/admin/users/${userId}/free-spins`, {
      params: { siteId, ...params },
    });
    return response.data;
  },
  giveUserSpin: async (userId: string, siteId: string | number, data: any): Promise<any> => {
    const response = await axiosInstance.post(`/admin/users/${userId}/free-spins`, data, {
      params: { siteId },
    });
    return response.data;
  },
  getUserSessions: async (id: string, params: any): Promise<any> => {
    const { page, limit, sortField, sortValue, ...rest } = params;
    const response = await axiosInstance.get(`/admin/users/${id}/sessions`, {
      params: {
        page,
        limit,
        sortField,
        sortValue,
        filters: '',
        ...rest,
      },
    });
    return response.data;
  },
  getUserRounds: async (id: string, params: any): Promise<any> => {
    const { page, limit, sortField, sortValue, ...rest } = params;
    const response = await axiosInstance.get(`/admin/users/${id}/rounds`, {
      params: {
        page,
        limit,
        sortField,
        sortValue,
        filters: '',
        ...rest,
      },
    });
    return response.data;
  },
  cancelUserSpin: async (
    userId: string,
    siteId: string | number,
    spinId: string | number,
  ): Promise<any> => {
    const response = await axiosInstance.post(
      `/admin/users/${userId}/free-spins/${spinId}/cancel`,
      {},
      {
        params: { siteId },
      },
    );
    return response.data;
  },
  blockUser: async (
    id: string | number,
    data: { durationHours: number; reason: string },
  ): Promise<any> => {
    const response = await axiosInstance.post(`/admin/users/${id}/block`, data);
    return response.data;
  },
  unblockUser: async (id: string | number): Promise<any> => {
    const response = await axiosInstance.post(`/admin/users/${id}/unblock`);
    return response.data;
  },
  toggleWithdraw: async (id: string | number, enabled: boolean): Promise<any> => {
    const response = await axiosInstance.patch(`/admin/users/${id}/withdraw-toggle`, { enabled });
    return response.data;
  },
  toggleDeposit: async (id: string | number, enabled: boolean): Promise<any> => {
    const response = await axiosInstance.patch(`/admin/users/${id}/deposit-toggle`, { enabled });
    return response.data;
  },
  toggleWithdrawManual: async (id: string | number, enabled: boolean): Promise<any> => {
    const response = await axiosInstance.patch(`/admin/users/${id}/withdraw-manual-toggle`, {
      enabled,
    });
    return response.data;
  },
  getSessionBalance: async (userId: string | number, sessionId: string | number): Promise<any> => {
    const response = await axiosInstance.get(
      `/admin/users/${userId}/sessions/${sessionId}/balance`,
    );
    return response.data;
  },
  getUserPaymentSummary: async (userId: string | number): Promise<any> => {
    const response = await axiosInstance.get(`/admin/users/${userId}/payment-summary`);
    return response.data;
  },
  getUserBonusOverview: async (
    userId: string | number,
    params?: { page?: number; limit?: number },
  ): Promise<any> => {
    const response = await axiosInstance.get(`/admin/users/${userId}/bonus-overview`, {
      params,
    });
    return response.data;
  },
  getUserTips: async (
    userId: string | number,
    params?: { type?: 'sent' | 'received'; page?: number; limit?: number },
  ): Promise<any> => {
    const response = await axiosInstance.get(`/admin/users/${userId}/tips`, { params });
    return response.data;
  },
  getNotes: async (id: string | number, params?: any): Promise<any> => {
    const response = await axiosInstance.get(`/admin/users/${id}/notes`, { params });
    return response.data;
  },
  createNote: async (id: string | number, text: string): Promise<any> => {
    const response = await axiosInstance.post(`/admin/users/${id}/notes`, { text });
    return response.data;
  },
  updateNote: async (id: string | number, noteId: string | number, text: string): Promise<any> => {
    const response = await axiosInstance.patch(`/admin/users/${id}/notes/${noteId}`, { text });
    return response.data;
  },
  deleteNote: async (id: string | number, noteId: string | number): Promise<any> => {
    const response = await axiosInstance.delete(`/admin/users/${id}/notes/${noteId}`);
    return response.data;
  },
};
